import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ImageAnnotation } from '@annotorious/react';
import { toast } from 'sonner';

import { PatchDetectionBodyLabel } from '@/api/generated';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription } from '@/components/ui/card';
import { ImageCrop } from '@/components/ui/image-crop';
import type { ImageNaturalSize } from '@/hooks/use-image-natural-size';
import {
  type AnnotationRect,
  type DetectionLabel,
  type DetectionSource,
  getAnnotationBorderClasses,
  getAnnotationProperties,
  getAnnotationRect,
} from '@/lib/annotations';
import { cn } from '@/lib/utils';

// Missing SKU is "None of the above" at the end of the predictions instead.
const LABEL_ACTIONS: ReadonlyArray<DetectionLabel> = [
  PatchDetectionBodyLabel.REMOVED,
];

export interface SelectedDetectionSummaryProps {
  annotation?: ImageAnnotation;
  imagePath: string;
  naturalSize: ImageNaturalSize | null;
  setLabel?: (label: DetectionLabel) => void;
  className?: string;
}

interface SelectedDetectionSummaryData {
  /** Backend product_detections.id; undefined for a drawn box not saved yet. */
  detectionId?: string;
  label: DetectionLabel;
  source: DetectionSource;
  confidence?: number;
  annotationRectangle: AnnotationRect;
}

const getSelectedDetectionSummaryData = (
  annotation: ImageAnnotation
): SelectedDetectionSummaryData => {
  const { label, source, confidence, temp } =
    getAnnotationProperties(annotation);
  const annotationRectangle = getAnnotationRect(annotation);

  return {
    detectionId: temp ? undefined : annotation.id,
    label,
    source,
    confidence,
    annotationRectangle,
  };
};

const SelectedDetectionSummaryPlaceholder = () => {
  const { t } = useTranslation();

  return (
    <div className='border border-dashed p-3 text-center text-xs'>
      {t('shelfDetail.noSelection')}
    </div>
  );
};

export const SelectedDetectionSummary: FC<SelectedDetectionSummaryProps> = ({
  annotation,
  imagePath,
  naturalSize,
  setLabel,
  className,
}: SelectedDetectionSummaryProps) => {
  const { t } = useTranslation();
  const detectionSummary = useMemo(
    () =>
      annotation ? getSelectedDetectionSummaryData(annotation) : undefined,
    [annotation]
  );
  const labelButtonClick = useCallback(
    (actionLabel: DetectionLabel) => setLabel?.(actionLabel),
    [setLabel]
  );
  const copyDetectionId = useCallback(
    (detectionId: string) => {
      navigator.clipboard.writeText(detectionId).then(
        () => toast.success(t('shelfDetail.detectionIdCopied')),
        () => toast.error(t('shelfDetail.detectionIdCopyFailed'))
      );
    },
    [t]
  );
  const borderColor = useMemo(
    () =>
      detectionSummary
        ? getAnnotationBorderClasses(detectionSummary.label)
        : '',
    [detectionSummary]
  );

  if (!detectionSummary) {
    return <SelectedDetectionSummaryPlaceholder />;
  }

  const { detectionId, label, source, annotationRectangle } = detectionSummary;

  return (
    <Card className={cn('p-2', borderColor, className)}>
      <CardContent className='flex flex-row gap-2 size-full'>
        <ImageCrop
          src={imagePath}
          className='size-full'
          imageCropRectangle={annotationRectangle}
          naturalSize={naturalSize}
        />
        <div className='flex flex-col gap-2'>
          {/* z-20 lifts it above the readonly overlay so the id can still be
              copied on completed shelves. */}
          {detectionId && (
            <button
              type='button'
              title={t('shelfDetail.copyDetectionId')}
              className='text-muted-foreground hover:text-foreground relative z-20 mb-1 cursor-copy text-right text-xs whitespace-nowrap'
              onClick={() => {
                copyDetectionId(detectionId);
              }}>
              {t('shelfDetail.detectionId')}{' '}
              <span className='font-mono'>{detectionId}</span>
            </button>
          )}
          {LABEL_ACTIONS.map((actionLabel) => (
            <Button
              key={actionLabel}
              type='button'
              size='sm'
              variant={label === actionLabel ? 'default' : 'outline'}
              onClick={() => labelButtonClick(actionLabel)}>
              {t(`shelfDetail.label.${actionLabel}`, {
                defaultValue: actionLabel,
              })}
            </Button>
          ))}

          <CardDescription className='mt-auto text-right'>
            <Badge variant={'outline'}>
              {t(`shelfDetail.source.${source}`, {
                defaultValue: source,
              })}
            </Badge>
          </CardDescription>
        </div>
      </CardContent>
    </Card>
  );
};
